import {
  Badge,
  makeStyles,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  Text,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import {
  ChartMultiple20Regular,
  CheckmarkCircle16Filled,
  Clock16Regular,
} from '@fluentui/react-icons';
import React from 'react';
import { useGetMetadataQuery } from '../../queries/recording';

const useStyles = makeStyles({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  surface: {
    padding: tokens.spacingVerticalM,
    minWidth: '280px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  section: {
    marginBottom: tokens.spacingVerticalM,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    marginBottom: tokens.spacingVerticalXS,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: tokens.spacingHorizontalS,
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: tokens.spacingVerticalXS,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  statValue: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  statLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  serviceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} 0`,
  },
  serviceName: {
    flex: 1,
    fontSize: tokens.fontSizeBase200,
  },
  badgeGroup: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalL,
  },
  badge: {
    marginRight: "5px"
  },
});

const StatBadge = ({
  value,
  type,
}: {
  value: number;
  type: 'pending' | 'running' | 'completed';
}) => {
  const styles = useStyles();
  const color =
    type === 'completed' ? 'success' : type === 'running' ? 'brand' : 'subtle';
  const icon =
    type === 'completed' ? (
      <CheckmarkCircle16Filled className={styles.badge} />
    ) : type === 'running' ? (
      <Spinner size="extra-tiny" className={styles.badge} />
    ) : (
      <Clock16Regular className={styles.badge} />
    );

  return (
    <Tooltip content={type === 'pending' ? 'En attente' : type === 'running' ? 'En cours' : 'Termine'} relationship="label">
      <Badge appearance="filled" color={color} size="small" icon={icon}>
        {value}
      </Badge>
    </Tooltip>
  );
};

export const StatsPopover = () => {
  const styles = useStyles();
  const { data, isPending } = useGetMetadataQuery();

  return (
    <Popover withArrow positioning="below-start">
      <PopoverTrigger disableButtonEnhancement>
        <Tooltip content="Statistiques du service" relationship="label">
          <div className={styles.trigger}>
            <ChartMultiple20Regular />
          </div>
        </Tooltip>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <div className={styles.header}>
          <ChartMultiple20Regular />
          <Text className={styles.headerTitle}>Statistiques du service</Text>
        </div>

        {isPending ? (
          <div className={styles.loadingContainer}>
            <Spinner size="small" label="Chargement..." />
          </div>
        ) : data?.service ? (
          <>
            <div className={styles.serviceRow}>
              <Text className={styles.serviceName}>Enregistrement</Text>
              <div className={styles.badgeGroup}>
                <StatBadge value={data.service.recorder.pending} type="pending" />
                <StatBadge value={data.service.recorder.running} type="running" />
                <StatBadge
                  value={data.service.recorder.completed}
                  type="completed"
                />
              </div>
            </div>

            <div className={styles.serviceRow}>
              <Text className={styles.serviceName}>Transcription</Text>
              <div className={styles.badgeGroup}>
                <StatBadge value={data.service.stt.pending} type="pending" />
                <StatBadge value={data.service.stt.running} type="running" />
                <StatBadge value={data.service.stt.completed} type="completed" />
              </div>
            </div>

            <div className={styles.serviceRow}>
              <Text className={styles.serviceName}>Compte-rendu</Text>
              <div className={styles.badgeGroup}>
                <StatBadge
                  value={data.service.summarizer.pending}
                  type="pending"
                />
                <StatBadge
                  value={data.service.summarizer.running}
                  type="running"
                />
                <StatBadge
                  value={data.service.summarizer.completed}
                  type="completed"
                />
              </div>
            </div>
          </>
        ) : (
          <Text>Aucune donnee disponible</Text>
        )}
      </PopoverSurface>
    </Popover>
  );
};
